import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Focus mode — a calm, single-block view for the block you're in right
// now: a live countdown, the linked task's checklist (tickable), and a
// pause / "end & log progress" pair. It reads data the app already has;
// "end & log progress" hands off to the calendar where the existing
// Complete? flow lives.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import BlockResources from "../components/BlockResources";
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
function dateStr(offsetDays) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
const BLOCK_LABEL = {
    lockout: "deep focus",
    flex: "flex",
    personal: "personal",
};
function fmtCountdown(ms) {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}
function fmtClock(iso) {
    const d = new Date(iso);
    let h = d.getHours();
    const mm = d.getMinutes().toString().padStart(2, "0");
    const ap = h < 12 ? "am" : "pm";
    if (h === 0)
        h = 12;
    else if (h > 12)
        h -= 12;
    return `${h}:${mm} ${ap}`;
}
export default function FocusPage() {
    const navigate = useNavigate();
    const [block, setBlock] = useState(null);
    const [checklist, setChecklist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [now, setNow] = useState(Date.now());
    // Pause is a display-only convenience — it freezes the countdown, it
    // doesn't move the real block. We track total paused time so the timer
    // resumes smoothly instead of jumping.
    const [paused, setPaused] = useState(false);
    const pauseStartRef = useRef(null);
    const pausedTotalRef = useRef(0);
    const load = useCallback(async () => {
        setError(null);
        try {
            const blocks = await api.get(`/time-blocks?from=${dateStr(0)}&to=${dateStr(1)}&tz=${encodeURIComponent(BROWSER_TZ)}`);
            const t = Date.now();
            const active = blocks.find((b) => {
                const s = new Date(b.start_at).getTime();
                const e = new Date(b.end_at).getTime();
                return t >= s && t < e;
            }) ?? null;
            setBlock(active);
            if (active && active.project_id && active.task_id) {
                try {
                    const proj = await api.get(`/projects/${active.project_id}`);
                    const task = proj.tasks.find((x) => x.id === active.task_id);
                    setChecklist(task?.checklist_items ?? []);
                }
                catch {
                    setChecklist([]);
                }
            }
            else {
                setChecklist([]);
            }
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load your focus block");
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        setLoading(true);
        void load();
    }, [load]);
    // Tick every second while running.
    useEffect(() => {
        if (paused)
            return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [paused]);
    function togglePause() {
        if (paused) {
            if (pauseStartRef.current != null) {
                pausedTotalRef.current += Date.now() - pauseStartRef.current;
                pauseStartRef.current = null;
            }
            setPaused(false);
        }
        else {
            pauseStartRef.current = Date.now();
            setPaused(true);
        }
    }
    async function toggleItem(item) {
        const next = !item.is_completed;
        setChecklist((cs) => cs.map((c) => (c.id === item.id ? { ...c, is_completed: next } : c)));
        try {
            await api.patch(`/tasks/checklist-items/${item.id}`, {
                is_completed: next,
            });
        }
        catch {
            // Revert on failure.
            setChecklist((cs) => cs.map((c) => c.id === item.id ? { ...c, is_completed: !next } : c));
        }
    }
    if (loading) {
        return (_jsx("div", { className: "page focus-page", children: _jsx("div", { className: "loading", children: "Loading focus mode\u2026" }) }));
    }
    if (error) {
        return (_jsx("div", { className: "page focus-page", children: _jsx("div", { className: "error", children: error }) }));
    }
    if (!block) {
        return (_jsx("div", { className: "page focus-page", children: _jsxs("div", { className: "focus-card focus-empty", children: [_jsx("h2", { children: "No block running right now" }), _jsx("p", { className: "muted", children: "Focus mode follows the block you're currently in. Start one from your calendar and come back." }), _jsxs("div", { className: "focus-actions", children: [_jsx("button", { type: "button", className: "btn-primary", onClick: () => navigate("/calendar"), children: "Open calendar" }), _jsx("button", { type: "button", onClick: () => navigate("/today"), children: "Back to Today" })] })] }) }));
    }
    // Effective timeline position — frozen while paused.
    const effNow = (paused && pauseStartRef.current != null
        ? pauseStartRef.current
        : now) - pausedTotalRef.current;
    const start = new Date(block.start_at).getTime();
    const end = new Date(block.end_at).getTime();
    const total = Math.max(end - start, 1);
    const elapsed = Math.min(Math.max(effNow - start, 0), total);
    const remaining = end - effNow;
    const pct = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
    const totalMin = Math.round(total / 60000);
    const elapsedMin = Math.round(elapsed / 60000);
    const kind = BLOCK_LABEL[block.block_type] ?? block.block_type;
    return (_jsx("div", { className: "page focus-page", children: _jsxs("div", { className: "focus-card", children: [_jsxs("p", { className: "focus-kicker", children: [_jsx("span", { className: `today-block-dot dot-${block.block_type}`, "aria-hidden": "true" }), kind, " \u00B7 ", fmtClock(block.start_at), " \u2013 ", fmtClock(block.end_at)] }), _jsx("h1", { className: "focus-title", children: block.title }), (block.project_name || block.task_title) && (_jsx("p", { className: "focus-sub", children: [block.project_name, block.task_title]
                        .filter(Boolean)
                        .join(" · ") })), _jsx("p", { className: `focus-timer${remaining <= 0 ? " is-over" : ""}`, children: remaining <= 0 ? "time's up" : fmtCountdown(remaining) }), _jsx("div", { className: "focus-progress-bar", children: _jsx("div", { className: "focus-progress-fill", style: { width: `${pct}%` } }) }), _jsxs("p", { className: "focus-elapsed muted small", children: [elapsedMin, " of ", totalMin, " minutes elapsed", paused && " · paused"] }), checklist.length > 0 && (_jsxs("div", { className: "focus-checklist", children: [_jsx("p", { className: "focus-checklist-head", children: "Checklist" }), checklist.map((item) => (_jsxs("label", { className: "focus-check-row", children: [_jsx("input", { type: "checkbox", checked: item.is_completed, onChange: () => toggleItem(item) }), _jsx("span", { className: item.is_completed ? "focus-check-done" : "", children: item.title })] }, item.id)))] })), _jsx(BlockResources, { projectId: block.project_id, variant: "rail" }), _jsxs("div", { className: "focus-actions", children: [_jsx("button", { type: "button", onClick: togglePause, children: paused ? "▶ Resume" : "⏸ Pause" }), _jsx("button", { type: "button", className: "btn-primary", onClick: () => navigate("/calendar"), children: "\u2713 End & log progress" })] }), _jsx("p", { className: "muted small focus-foot", children: "\u201CEnd & log progress\u201D opens this block on your calendar so you can mark how far you got." })] }) }));
}
